import { useAuthOptional } from './useAuth';
import { DEFAULT_TIMEZONE } from '@/lib/dateFormat';

/**
 * The org's configured display timezone (Settings → Organization), defaulting to
 * UTC for an org that never set one, while `useAuth()` is still loading, or when
 * rendered outside an `AuthProvider` (e.g. a component test that mounts a
 * dashboard surface in isolation) — every one of those is "identity unknown",
 * and UTC is the platform default regardless of which reason applies.
 */
export function useOrgTimezone(): string {
  const auth = useAuthOptional();
  return usableTimezone(auth?.organization?.timezone);
}

/**
 * The stored zone, or UTC when this browser cannot format in it.
 *
 * The server validates on write, but it validates against ITS ICU build - a name
 * that Node canonicalizes and an older browser rejects passes that check and
 * then throws `RangeError: Invalid time zone specified` inside render. That was
 * survivable while only page CONTENT formatted timestamps; the clock put it in
 * `TopBar`, which is on every dashboard route, so an unusable value would take
 * the whole shell down rather than one panel. The client is the trust boundary
 * for what the client can render.
 */
export function usableTimezone(timezone: string | null | undefined): string {
  if (!timezone) return DEFAULT_TIMEZONE;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return timezone;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}
